import React, { FC, useEffect, useId, useRef, useState } from 'react';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import SVGInline from 'components/SVGInline/SVGInline';
import { getLanguage } from 'utils/fnData';
import { Person } from 'types/Asset';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { useBackdropClose } from 'hooks/useBackdropClose/useBackdropClose';
import icons from 'style';
import { withNoScroll } from 'hooks/withNoScroll/withNoScroll';
import translate from 'utils/fnTranslate';
import { ModalCloseButton, ModalNavigation, ModalWrapper } from 'style/styled-components/reusable.css';
import {
    ChannelMeta,
    DetailMoreAudio,
    DetailMoreAudioAndSubtitle,
    DetailMoreCast,
    DetailMoreSubtitle,
    GradientBottom,
    MetadataWrapper,
    ModalContent,
    ModalContentWrapper,
    ModalDetails,
    ModalSubtitle,
    ModalTitle,
    ModalTitleWrapper,
    ModalMarkerWrapper,
} from './DetailMoreModal.css';

type PropTypes = {
    modalTitle?: string;
    modalSubTitle?: string;
    metadata?: string;
    showMoreMetadataTitle?: any[];
    showMoreMetadataContent?: any[];
    cast?: Person[];
    directors?: Person[];
    modalText?: string;
    closeOverlay: () => void;
    caption?: string[] | null;
    original?: string[] | null;
    iconList?: React.ReactNode[];
    contentMarker?: React.ReactNode;
};

const DetailMoreModalComponent: FC<PropTypes> = ({
    modalTitle,
    modalSubTitle,
    metadata,
    showMoreMetadataTitle,
    showMoreMetadataContent,
    cast = [],
    directors = [],
    modalText,
    closeOverlay,
    caption,
    original,
    iconList,
    contentMarker,
}) => {
    const scrollbarRef = useRef<HTMLDivElement>(null);
    const contentScrollbarRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState<boolean>(true);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const backdropClose = useBackdropClose(closeOverlay);

    const titleId = useId();

    useKeyboardShortcuts({
        backspace: closeOverlay,
    });

    const hasAdditionalContentDetails = () =>
        cast?.length > 0 ||
        directors?.length > 0 ||
        (original !== null && original?.length > 0) ||
        (caption !== null && caption?.length > 0) ||
        showMoreMetadataTitle ||
        showMoreMetadataContent;

    useEffect(() => {
        if (scrollbarRef?.current && contentScrollbarRef?.current) {
            setIsVisible(true);
            setIsScrollable(contentScrollbarRef?.current?.clientHeight > scrollbarRef?.current?.clientHeight);
        } else {
            setIsVisible(false);
            setIsScrollable(!isScrollable);
        }
    }, [scrollbarRef]);

    return (
        <ModalDialog
            as={ModalWrapper}
            onClose={closeOverlay}
            dialogRef={scrollbarRef}
            opacity={0.9}
            aria-labelledby={titleId}
            tabIndex={isScrollable ? 0 : -1}
            aria-modal
            {...backdropClose}
        >
            <ModalNavigation {...backdropClose}>
                <ModalCloseButton
                    type="button"
                    data-test-id={'tv-detailscreen-closeoverlaybutton'}
                    aria-label={translate('CLOSE_BUTTON')}
                    onClick={closeOverlay}
                    tabIndex={0}
                >
                    <SVGInline svg={icons.closeIcon} />
                </ModalCloseButton>
            </ModalNavigation>
            <ModalDetails isVisible={isVisible} isScrollable={isScrollable} ref={contentScrollbarRef} {...backdropClose}>
                <ModalTitleWrapper>
                    <ModalTitle id={titleId} data-test-id={'tv-detailscreen-modaltitleoverlay'}>
                        {modalTitle}
                    </ModalTitle>
                    <ModalSubtitle data-test-id={'tv-detailscreen-modalsubtitleoverlay'}>{modalSubTitle}</ModalSubtitle>
                    {iconList?.length > 0 || contentMarker ? (
                        <ModalMarkerWrapper>
                            {contentMarker}
                            {iconList}
                        </ModalMarkerWrapper>
                    ) : null}

                    {showMoreMetadataTitle && showMoreMetadataContent ? (
                        <MetadataWrapper>
                            <ChannelMeta>
                                {showMoreMetadataTitle} {showMoreMetadataContent}
                            </ChannelMeta>
                        </MetadataWrapper>
                    ) : (
                        <MetadataWrapper>
                            <ChannelMeta>{metadata}</ChannelMeta>
                        </MetadataWrapper>
                    )}

                    <ModalContent data-test-id={'tv-detailscreen-descriptionoverlay'}>{modalText}</ModalContent>
                </ModalTitleWrapper>
                {hasAdditionalContentDetails() && (
                    <ModalContentWrapper>
                        <ModalContent>
                            {cast?.length > 0 && (
                                <DetailMoreCast>
                                    {/* l33t h4x0r */}
                                    <strong>
                                        {translate('SCREEN_MORE_INFO_ACTORS')?.includes(':')
                                            ? translate('SCREEN_MORE_INFO_ACTORS')
                                            : `${translate('SCREEN_MORE_INFO_ACTORS')}:`}
                                    </strong>{' '}
                                    {cast.map((item) => (item.person ? item.person.name : item.title)).join(', ')}
                                </DetailMoreCast>
                            )}
                            {directors?.length > 0 && (
                                <DetailMoreCast>
                                    {/* l33t h4x0r */}
                                    <strong>
                                        {translate('SCREEN_MORE_INFO_DIRECTORS')?.includes(':')
                                            ? translate('SCREEN_MORE_INFO_DIRECTORS')
                                            : `${translate('SCREEN_MORE_INFO_DIRECTORS')}:`}
                                    </strong>{' '}
                                    {directors.map((item) => (item.person ? item.person.name : item.title)).join(', ')}
                                </DetailMoreCast>
                            )}
                        </ModalContent>
                        <DetailMoreAudioAndSubtitle isScrollable={isScrollable}>
                            {original !== null && original?.length > 0 && (
                                <DetailMoreAudio>
                                    <strong>{translate('DETAIL_AUDIO_LANGUAGE')} </strong>
                                    {original?.map((audio) => getLanguage(audio, translate(`LANGUAGE_${audio.toUpperCase()}`))).join(', ')}
                                </DetailMoreAudio>
                            )}
                            {caption !== null && caption !== undefined && (
                                <DetailMoreSubtitle>
                                    <strong>{translate('DETAIL_SUBTITLES')} </strong>
                                    {caption?.length > 0
                                        ? caption
                                              .map((subtitle) => getLanguage(subtitle, translate(`LANGUAGE_${subtitle.toUpperCase()}`)))
                                              .join(', ')
                                        : translate('SUBTITLES_NONE')}
                                </DetailMoreSubtitle>
                            )}
                        </DetailMoreAudioAndSubtitle>
                    </ModalContentWrapper>
                )}
            </ModalDetails>
            <GradientBottom {...backdropClose} />
        </ModalDialog>
    );
};

export const DetailMoreModal = withNoScroll(DetailMoreModalComponent);
